<template>
  <v-row no-gutters align-content="start" class="raster-comparison-wrapper">
    <v-col cols="12">
      <cirrus-comparison-toolbar :name="name"
        :context="context"
        :exams="exams"
        :groups="groups" />
    </v-col>
    <v-col cols="12" class="raster-state-controls">
      <v-row no-gutters class="raster-state-controls-row">
        <v-col cols="5" class="raster-drawer-wrapper">
          <raster-image-drawer
            :drawer-id="name + '-raster-thumbnail-drawer'"
            :slices="slices"
            :sliceCurrentIndex="sliceCurrentIndex"
            :octDataLoading="octDataLoading"
            :octColorSettings="octColorSettings"
            :rasterThumbSize="rasterThumbSize"
            @selectSlice="selectSlice"/>
        </v-col>
        <v-col cols="7" class="raster-reference-wrapper">
          <oct-reference-image
            :playbackTop="true"
            :reference-id="name + '-reference-image-wrapper'"
            :isLoading="refImgLoading || octDataLoading"
            :refImg="refImg"
            :refFit="refFit"
            :refImgNotFound="refImgNotFound"
            :refImgLocation="refImgLocation"
            :slices="slices"
            :sliceCurrent="sliceCurrent"
            :sliceBounds="sliceBounds"
            :sliceCurrentIndex="sliceCurrentIndex"
            :isPlaying="isPlaying"
            :playRate="playRate"
            :showExtension="true"
            :drawAllSlices="true"
            @previous="previousSlice"
            @next="nextSlice"
            @play="play"
            @pause="pause"
            @setPlayRate="setPlayRate"
            @selectSlice="selectSlice"/>
          <raster-color-buttons
            :isInverted="isInverted"
            :hasColorPalette="hasColorPalette"
            @toggleColor="toggleColor"
            @invertColor="invertColor"/>
        </v-col>
      </v-row>
    </v-col>
    <v-col cols="12" class="raster-slice-view">
      <oct-slice-view
        :slice-id="name + '-slice-image-wrapper'"
        :fitSize="sliceFit"
        :img="sliceImg"
        :imgSize="sliceImgSize"
        :octColorSettings="octColorSettings"
        :imgLoading="sliceImgLoading"
        :dataLoading="octDataLoading"
        :sliceEyeMarker="sliceCurrentEyeMarker"
        :imgNotFound="sliceImgNotFound"
        :sliceInfo="sliceInfo"/>
    </v-col>
  </v-row>
</template>

<script>
import cirrusComparisonToolbar from "./CirrusComparisonToolbar";
import octReferenceImage from "./OCTReferenceImage";
import rasterImageDrawer from "./RasterImageDrawer";
import rasterColorButtons from "./RasterColorButtons";
import octSliceView from "./OCTSliceView";
import responsiveCanvases from "../../mixins/ResponsiveCanvases";

import {
  defaultSize,
  viewOffset,
  withinViewBounds,
  calculateRealLocation,
  defaultTransform,
  setFittedSize,
  getViewSize,
  setSize,
  setTransformSizes
} from "../../../canvas/geometry";
import { mapWatchers } from "../../../helpers/utils";

export default {
  name: "rasterComparisonView",
  components: {
    cirrusComparisonToolbar,
    octReferenceImage,
    rasterImageDrawer,
    rasterColorButtons,
    octSliceView
  },
  mixins: [responsiveCanvases],
  props: {
    name: String,
    context: Object,
    exams: Array,
    groups: Array
  },
  data() {
    return {
      refSize: defaultSize(),
      refFit: defaultSize(),
      sliceSize: defaultSize(),
      sliceFit: defaultSize(),
      sliceImgSize: defaultSize(),
      rasterThumbSize: defaultSize(),
      transform: defaultTransform()
    };
  },
  computed: {
    octDataLoading() {
      return this.context && this.context.octDataLoading;
    },
    referenceImage() {
      return this.context && this.context.referenceImage;
    },
    refImgLoading() {
      return (
        this.referenceImage &&
        (this.referenceImage.imgIsLoading || this.referenceImage.imgIsQueued)
      );
    },
    refImgNotFound() {
      return !!(this.referenceImage && this.referenceImage.imgNotFound);
    },
    refImg() {
      return this.referenceImage && this.referenceImage.imgHtmlObject;
    },
    slices() {
      if (!this.context) return [];
      return this.$store.getters.getSlices(
        "slice",
        this.context.exSrNo,
        this.context.groupName
      );
    },
    sliceBounds() {
      return this.context && this.context.sliceBounds;
    },
    sliceInfo() {
      return this.context && this.context.sliceInfo;
    },
    isPlaying() {
      return !!(this.context && this.context.playIntervalId);
    },
    playRate() {
      return this.context && this.context.playRate;
    },
    sliceCurrent() {
      return this.context && this.context.sliceCurrent;
    },
    sliceCurrentIndex() {
      return this.sliceCurrent && this.sliceCurrent.index;
    },
    sliceCurrentEyeMarker() {
      return this.sliceCurrent && this.sliceCurrent.eyeMarker;
    },
    sliceImg() {
      return this.sliceCurrent && this.sliceCurrent.imgHtmlObject;
    },
    sliceImgNotFound() {
      return !!(this.sliceCurrent && this.sliceCurrent.imgNotFound);
    },
    sliceImgLoading() {
      return (
        this.sliceCurrent &&
        (this.sliceCurrent.imgIsLoading || this.sliceCurrent.imgIsQueued)
      );
    },
    octColorSettings() {
      return this.context && this.context.octColorSettings;
    },
    hasColorPalette() {
      return this.octColorSettings && !!this.octColorSettings.colorPalette;
    },
    isInverted() {
      return this.octColorSettings && this.octColorSettings.isInverted;
    },
    scanDisplayRatio() {
      if (this.$store.getters.cirrusScanDisplayRatio && this.$store.getters.cirrusScanDisplayRatio != "default")
        return this.$store.getters.cirrusScanDisplayRatio;
      else
        return this.$store.getters.sitecirrusScanDisplayRatio;
    }
  },
  methods: {
    updateCanvasSizes() {
      this.referenceWrapperId = this.name + "-reference-image-wrapper";
      this.sliceWrapperId = this.name + "-slice-image-wrapper";
      this.drawerId = this.name + "-raster-thumbnail-drawer";

      // reference control size
      let rvs = getViewSize(this.referenceWrapperId);
      setSize(this.refSize, rvs);

      // fit the reference image within at least a 1:1.25 aspect ratio
      let aspectFit = { h: rvs.h, w: Math.min(rvs.w, rvs.h * 1.25) };
      setFittedSize(this.refSize, aspectFit, this.refFit, 15);

      let svs = getViewSize(this.sliceWrapperId);
      setSize(this.sliceSize, svs);

      if (this.scanDisplayRatio == "stretch") {
        // fit the slice image within at most a 1.25:1 aspect ratio
        aspectFit = { h: Math.min(svs.h, svs.w * 0.8), w: svs.w };
        setSize(this.sliceImgSize, aspectFit);
      }
      else {
        let aspectRatio = this.sliceInfo.dimensions.pixelWidth > this.sliceInfo.dimensions.pixelHeight ?
          (this.sliceInfo.dimensions.pixelHeight / this.sliceInfo.dimensions.pixelWidth) :
          (this.sliceInfo.dimensions.pixelWidth / this.sliceInfo.dimensions.pixelHeight);
        if (svs.w * aspectRatio < svs.h) {
          setSize(this.sliceImgSize, { w: svs.w, h: svs.w * aspectRatio });
        } else {
          setSize(this.sliceImgSize, { w: svs.h / aspectRatio, h: svs.h });
        }
      }

      setFittedSize(this.sliceSize, this.sliceImgSize, this.sliceFit, 15);

      // enforce same aspect ratio for the thumbnails
      let dvs = getViewSize(this.drawerId);
      let maxRoom = { h: 200, w: dvs.w - 30 };
      setFittedSize(maxRoom, aspectFit, this.rasterThumbSize, 5);

      setTransformSizes(this.transform, this.refFit, this.refFit);
    },
    refImgLocation(x, y) {
      const offset = viewOffset(this.refSize, this.refFit);
      if (!withinViewBounds(x, y, this.refSize, offset))
        return { x: -1, y: -1 };
      let loc = calculateRealLocation(x, y, offset, this.transform);
      return { x: loc.x / this.refFit.w, y: loc.y / this.refFit.h };
    },
    previousSlice() {
      this.$store.dispatch("selectPreviousSliceCompare", this.name);
    },
    nextSlice() {
      this.$store.dispatch("selectNextSliceCompare", this.name);
    },
    play() {
      this.$store.dispatch("playSlicesCompare", this.name);
    },
    pause() {
      this.$store.commit("pauseCompare", this.name);
    },
    setPlayRate(rate) {
      this.$store.commit("setPlayRateCompare", { view: this.name, rate });
    },
    selectSlice(index) {
      this.$store.dispatch("selectOCTImageByIndexCompare", {
        view: this.name,
        keyword: "slice",
        index
      });
    },
    toggleColor() {
      this.$store.dispatch("toggleColorCompare", this.name);
    },
    invertColor() {
      this.$store.dispatch("toggleInvertedCompare", this.name);
    }
  },
  watch: {
    ...mapWatchers(["refImg", "sliceInfo"], function() {
      this.updateCanvasSizes();
    })
  },
  mounted() {
    this.updateCanvasSizes();
  }
};
</script>